/**
 * From FileList prefix under output, return the selected S3 log folder.
 *
 * Supported layouts:
 *   - legacy: `logs/<taskId>/`
 *   - pool-scoped: `logs/<poolId>/<taskId>/`
 *
 * Returns null if the prefix is not inside a concrete execution folder. When
 * the user is only at `logs/` or `logs/<poolId>/`, the "Merge S3 logs" button
 * should remain disabled because there are no JSON files at that level.
 */
export function parseS3LogSelectionFromOutputPrefix(prefix) {
  if (prefix == null || typeof prefix !== 'string') return null;
  const parts = prefix.replace(/\/$/, '').split('/').filter(Boolean);
  const i = parts.indexOf('logs');
  if (i === -1) return null;
  const rest = parts.slice(i + 1);
  if (rest.length === 0) return null;

  // Legacy layout: logs/<taskId>/
  if (rest.length === 1) {
    const taskId = String(rest[0] || '').trim();
    return taskId ? { taskId, prefix: `logs/${taskId}/`, poolId: null } : null;
  }

  // Pool layout: logs/<poolId>/<taskId>/...
  const poolId = String(rest[0] || '').trim();
  const taskId = String(rest[1] || '').trim();
  if (!poolId || !taskId) return null;
  return {
    taskId,
    poolId,
    prefix: `logs/${poolId}/${taskId}/`,
  };
}

/**
 * Back-compat wrapper for callers that only need the task id segment.
 */
export function parseTaskIdFromOutputPrefix(prefix) {
  return parseS3LogSelectionFromOutputPrefix(prefix)?.taskId ?? null;
}

/**
 * Load unified execution logs from S3 after Lambdas flush buffers.
 *
 * Defaults to the current pool-scoped canonical layout
 * `output/logs/{poolId}/{taskId}/*.json` only when a caller provides
 * `prefix`. For legacy callers without a prefix, preserve the old
 * `output/logs/{taskId}/*.json` path.
 *
 * Returns full structured rows (same shape as WebSocket trace events) for the Execution Trace viewer.
 */
export async function fetchMergedS3ExecutionLogs({ apiBaseUrl, networkId, taskId, idToken, prefix }) {
  if (!networkId || !taskId || !idToken) {
    throw new Error('networkId, taskId, and auth token are required');
  }
  const effectivePrefix =
    typeof prefix === 'string' && prefix.trim()
      ? prefix.trim().replace(/^\/+/, '').replace(/\/?$/, '/')
      : `logs/${taskId}/`;
  const listUrl = `${apiBaseUrl}/networks/${networkId}/files?type=output&prefix=${encodeURIComponent(effectivePrefix)}`;
  const lr = await fetch(listUrl, { headers: { Authorization: `Bearer ${idToken}` } });
  if (!lr.ok) {
    throw new Error(`List logs failed: ${lr.statusText}`);
  }
  const listData = await lr.json();
  const files = listData.files || [];

  const rows = [];
  for (const file of files) {
    if (!file.key || !file.key.endsWith('.json')) continue;
    const dr = await fetch(
      `${apiBaseUrl}/networks/${networkId}/files/download?key=${encodeURIComponent(file.key)}`,
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    if (!dr.ok) continue;
    const { downloadUrl } = await dr.json();
    if (!downloadUrl) continue;
    const gr = await fetch(downloadUrl);
    if (!gr.ok) continue;
    let arr;
    try {
      arr = JSON.parse(await gr.text());
    } catch {
      continue;
    }
    if (!Array.isArray(arr)) continue;
    for (const row of arr) {
      if (row && typeof row === 'object') {
        rows.push({
          timestamp: row.timestamp,
          level: row.level || 'INFO',
          message: row.message != null ? String(row.message) : '',
          taskId: row.taskId || taskId,
          agentId: row.agentId,
          eventType: row.event_type ?? row.eventType,
          component: row.component,
          data: row.data && typeof row.data === 'object' ? row.data : row.data != null ? { value: row.data } : undefined
        });
      }
    }
  }

  rows.sort((a, b) => String(a.timestamp || '').localeCompare(String(b.timestamp || '')));
  return rows;
}
